import { formatQueueTimestamp } from '../dto-mappers';
import type { DealerInProgressEvent } from '../../models/entities/dealer-api.model';
import { resolveProcessingClaimedAt } from '../../utils/processing-claimed-at.util';
import type { EventType } from '../../models/entities/event-detail.model';
import type {
  AgentOpenWorkResponse,
  EventsInProcessResponse,
} from '../../models/entities/events-in-process.model';
import type { PatrolListResponse } from '../../models/entities/patrol-list.model';

const EVENT_TYPE_ALARM = 0;
const EVENT_TYPE_PATROL = 1;
const EVENT_TYPE_SITE_CHECK = 2;

const PROCESSING_MODE_BY_EVENT_TYPE: Record<number, EventType> = {
  [EVENT_TYPE_ALARM]: 'process-event',
  [EVENT_TYPE_PATROL]: 'manual-patrol',
  [EVENT_TYPE_SITE_CHECK]: 'site-check',
};

const ISO_WITHOUT_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/;

function parseInProgressUtcInstant(value: string): Date {
  return new Date(ISO_WITHOUT_OFFSET.test(value) ? `${value}Z` : value);
}

function resolveDisplayTimeZone(timezone?: string | null): string {
  const requested = timezone?.trim();
  if (requested) {
    try {
      new Intl.DateTimeFormat('en-GB', { timeZone: requested }).format(new Date());
      return requested;
    } catch {
      // Invalid IANA — in-progress tables show UTC, not the browser zone.
    }
  }
  return 'UTC';
}

function displayTimestamp(value: string | null | undefined, timezone?: string | null): string {
  const trimmed = value?.trim();
  if (!trimmed) {
    return '';
  }

  const date = parseInProgressUtcInstant(trimmed);
  if (Number.isNaN(date.getTime())) {
    return trimmed;
  }

  return formatQueueTimestamp(date.toISOString(), resolveDisplayTimeZone(timezone));
}

/** Maps Dealer in-progress alarm events (eventTypeId 0) into the Events In Process table. */
export function mapDealerInProgressToEventsInProcess(
  events: DealerInProgressEvent[] | null | undefined,
): EventsInProcessResponse {
  const items = (events ?? [])
    .filter((event) => event.eventTypeId === EVENT_TYPE_ALARM)
    .map((event) => ({
      id: event.eventId,
      firstAlarmDetected: displayTimestamp(
        event.firstAlarmDateTime ?? event.createdDateTime,
        event.siteTimeZone,
      ),
      location: event.siteName?.trim() || `Site ${event.siteId}`,
      priority: Number(event.priority ?? 0),
      alarmCount: Number(event.alarmCount),
      siteId: Number(event.siteId),
      agent: event.currentAssignedUserName?.trim() ?? '',
      userId: event.currentAssignedUserId ?? undefined,
      type: 'process-event' as const,
      ...(event.claimedDateTime?.trim()
        ? { claimedAt: resolveProcessingClaimedAt(event.claimedDateTime) }
        : {}),
    }));

  return {
    totalCount: items.length,
    items,
  };
}

/** Maps Dealer in-progress patrol/site-check events (eventTypeId 1|2) into the Patrol table. */
export function mapDealerInProgressToPatrolList(
  events: DealerInProgressEvent[] | null | undefined,
): PatrolListResponse {
  const items = (events ?? [])
    .filter(
      (event) =>
        event.eventTypeId === EVENT_TYPE_PATROL || event.eventTypeId === EVENT_TYPE_SITE_CHECK,
    )
    .map((event) => ({
      id: event.eventId,
      patrolStarted: displayTimestamp(
        event.claimedDateTime ?? event.createdDateTime,
        event.siteTimeZone,
      ),
      location: event.siteName?.trim() || `Site ${event.siteId}`,
      patrolNote: event.eventTypeId === EVENT_TYPE_SITE_CHECK ? 'Site Check' : 'Manual Patrol',
      agent: event.currentAssignedUserName?.trim() ?? '',
      status: 'in-progress' as const,
      siteId: event.siteId,
    }));

  return {
    totalCount: items.length,
    items,
  };
}

/**
 * Maps every in-progress event, whatever its type, into the agent's open work list.
 * Used to reopen tabs on load — unlike the in-process table, patrols must survive here.
 */
export function mapDealerInProgressToAgentOpenWork(
  events: DealerInProgressEvent[] | null | undefined,
): AgentOpenWorkResponse {
  const items = (events ?? []).map((event) => ({
    id: event.eventId,
    firstAlarmDetected: event.createdDateTime,
    location: event.siteName?.trim() || `Site ${event.siteId}`,
    priority: Number(event.priority ?? 0),
    alarmCount: Number(event.alarmCount),
    siteId: Number(event.siteId),
    agent: event.currentAssignedUserName?.trim() ?? '',
    userId: event.currentAssignedUserId ?? undefined,
    type: PROCESSING_MODE_BY_EVENT_TYPE[event.eventTypeId] ?? 'process-event',
    ...(event.claimedDateTime?.trim()
      ? { claimedAt: resolveProcessingClaimedAt(event.claimedDateTime) }
      : {}),
  }));

  return {
    totalCount: items.length,
    items,
  };
}
